/** Minimal description of a raster's coordinate reference system, read from GeoTIFF keys. */
export interface CrsInfo {
  /** EPSG code of the projected (preferred) or geographic CRS, when the file declares one. */
  epsg?: number;
  /** Human readable name, from the citation keys or the EPSG code. */
  name: string;
  /** False when the file is in lon/lat degrees, which this viewer does not project. */
  projected: boolean;
  /** Metres per CRS linear unit (1 for metre, 0.3048 for international foot, …). */
  unitToMetre: number;
}

/** GeoTIFF `ProjLinearUnitsGeoKey` / EPSG unit-of-measure codes seen in the wild. */
const LINEAR_UNITS: Record<number, { name: string; toMetre: number }> = {
  9001: { name: "metre", toMetre: 1 },
  9002: { name: "foot", toMetre: 0.3048 },
  9003: { name: "US survey foot", toMetre: 1200 / 3937 },
  9005: { name: "Clarke's foot", toMetre: 0.3047972654 },
  9036: { name: "kilometre", toMetre: 1000 },
  9093: { name: "statute mile", toMetre: 1609.344 },
};

/** Geo keys as returned by geotiff.js `GeoTIFFImage.getGeoKeys()`. */
export interface GeoKeys {
  GTModelTypeGeoKey?: number;
  GTRasterTypeGeoKey?: number;
  GTCitationGeoKey?: string;
  GeographicTypeGeoKey?: number;
  GeogCitationGeoKey?: string;
  ProjectedCSTypeGeoKey?: number;
  PCSCitationGeoKey?: string;
  ProjLinearUnitsGeoKey?: number;
  VerticalCSTypeGeoKey?: number;
  VerticalCitationGeoKey?: string;
  [key: string]: unknown;
}

/** `GTModelTypeGeoKey` value 1 = projected 2D, 2 = geographic 2D, 3 = geocentric. */
const MODEL_PROJECTED = 1;
const MODEL_GEOGRAPHIC = 2;

export function readCrs(geoKeys: GeoKeys | undefined | null): CrsInfo {
  const keys = geoKeys ?? {};
  const epsg =
    typeof keys.ProjectedCSTypeGeoKey === "number" && keys.ProjectedCSTypeGeoKey !== 32767
      ? keys.ProjectedCSTypeGeoKey
      : typeof keys.GeographicTypeGeoKey === "number" && keys.GeographicTypeGeoKey !== 32767
        ? keys.GeographicTypeGeoKey
        : undefined;

  const projected =
    keys.GTModelTypeGeoKey === MODEL_PROJECTED
      ? true
      : keys.GTModelTypeGeoKey === MODEL_GEOGRAPHIC
        ? false
        : // Not all writers set the model type; presence of a projected CS key implies it.
          typeof keys.ProjectedCSTypeGeoKey === "number";

  const citation = firstString(
    keys.PCSCitationGeoKey,
    keys.GTCitationGeoKey,
    keys.GeogCitationGeoKey,
  );
  const unit = LINEAR_UNITS[keys.ProjLinearUnitsGeoKey ?? 9001];

  const name = citation?.replace(/\|$/, "").trim() || (epsg ? `EPSG:${epsg}` : "unknown CRS");
  return {
    epsg,
    name,
    projected,
    unitToMetre: projected ? (unit?.toMetre ?? 1) : 1,
  };
}

export function describeCrs(crs: CrsInfo | undefined): string {
  if (!crs) return "unknown";
  const parts = [crs.name];
  if (crs.epsg && !crs.name.includes(String(crs.epsg))) parts.push(`EPSG:${crs.epsg}`);
  return parts.join(" · ");
}

/**
 * Datasets are assumed to share one projected CRS (no reprojection is performed), so a
 * mismatch is reported to the user rather than silently producing misaligned layers.
 * Returns a warning string, or null when the two are compatible.
 */
export function crsMismatch(a: CrsInfo | undefined, b: CrsInfo | undefined): string | null {
  if (!a || !b) return null;
  if (a.epsg && b.epsg) {
    return a.epsg === b.epsg ? null : `CRS mismatch: EPSG:${a.epsg} vs EPSG:${b.epsg}`;
  }
  if (a.projected !== b.projected) return "CRS mismatch: projected vs geographic coordinates";
  const na = normalise(a.name);
  const nb = normalise(b.name);
  if (na && nb && na !== nb) return `CRS mismatch: "${a.name}" vs "${b.name}"`;
  return null;
}

function normalise(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function firstString(...values: unknown[]): string | undefined {
  for (const v of values) if (typeof v === "string" && v.trim()) return v;
  return undefined;
}
